import { getCollectionReport } from 'lib/api/reports';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { errorHandler } from 'lib/functions/errors';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

const PERMITTED_DOCUMENT_TYPES = [
  { value: 'electronic-invoice', label: 'Factura electrónica' },
  {
    value: 'electronic-invoice-confirming',
    label: 'Factura electrónica de confirming',
  },
  { value: 'buy-order', label: 'Orden de compra' },
  { value: 'check-invoice', label: 'Factura de cheque' },
  { value: 'contract', label: 'Contrato' },
  { value: 'check', label: 'Cheque' },
  { value: 'pending-account', label: 'Cuenta pendiente' },
  { value: 'credit-request-cuota', label: 'Cuota de crédito' },
  { value: 'promissory-note', label: 'Pagaré' },
  { value: 'renegotiation', label: 'Renegociación' },
];

export const PERMITTED_CONCILIATIONS_DOCUMENT_TYPES = [
  { value: 'fund', label: 'Fondo' },
  { value: 'discount', label: 'Descuento' },
  { value: 'credit', label: 'Aplicación en crédito' },
  { value: 'factoring', label: 'Aplicación en factoring' },
  { value: 'passive', label: 'Devolución de excedentes' },
  { value: 'payIn', label: 'Recaudación' },
];

const Collection: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [clientIdentifier, setClientIdentifier] = useState('');
  const [clientName, setClientName] = useState('');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [stakeholderIdentifier, setStakeholderIdentifier] = useState('');
  const [stakeholderName, setStakeholderName] = useState('');
  const [documentType, setDocumentType] = useState([]);
  const [collectionType, setCollectionType] = useState([]);
  const [folio, setFolio] = useState('');
  const [data, setData] = useState([]);

  const filters: Filter[] = [
    {
      label: 'Fecha de inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre Cliente',
      type: 'autocomplete',
      value: clientName,
      key: 'clientName',
      setter: setClientName,
    },
    {
      label: 'Rut Cliente',
      type: 'text',
      value: clientIdentifier,
      setter: setClientIdentifier,
    },
    {
      label: 'Nombre Emisor',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut Emisor',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre Deudor',
      type: 'autocomplete',
      value: stakeholderName,
      key: 'stakeholderName',
      setter: setStakeholderName,
    },
    {
      label: 'Rut Deudor',
      type: 'text',
      value: stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      label: 'Folio',
      type: 'text',
      value: folio,
      setter: setFolio,
    },
    {
      label: 'Tipo de documento',
      type: 'multiple',
      value: documentType,
      setter: setDocumentType,
      options: PERMITTED_DOCUMENT_TYPES,
    },
    {
      label: 'Tipo de recaudación',
      type: 'multiple',
      value: collectionType,
      setter: setCollectionType,
      options: PERMITTED_CONCILIATIONS_DOCUMENT_TYPES,
    },
  ];

  const generateQuery = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (businessName) params.append('businessName', businessName);
    if (stakeholderName) params.append('stakeholderName', stakeholderName);
    if (businessIdentifier)
      params.append('businessIdentifier', businessIdentifier);
    if (stakeholderIdentifier)
      params.append('stakeholderIdentifier', stakeholderIdentifier);
    if (clientName) params.append('clientName', clientName);
    if (clientIdentifier) params.append('clientIdentifier', clientIdentifier);
    if (folio) params.append('folio', folio);
    if (documentType.length) {
      params.append(
        'documentType',
        documentType.map((type: any) => type.value).join(','),
      );
    }
    if (collectionType.length) {
      params.append(
        'collectionType',
        collectionType.map((type: any) => type.value).join(','),
      );
    }
    return params.toString();
  };

  const fetchData = async () => {
    const query = generateQuery();
    const response = await getCollectionReport(dispatch, query);
    setData(response?.conciliations || []);
    return response;
  };

  const triggerSearch = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      errorHandler(
        dispatch,
        error,
        'Error al obtener el reporte de recaudaciones',
      );
    } finally {
      setLoading(false);
    }
  };

  const setUp = async () => {
    setLoading(true);
    try {
      await fetchData();
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener los datos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void setUp();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        variant="body1"
        title="Acá verás todas las recaudaciones recibidas de un documento."
        buttons={[
          {
            name: 'Buscar',
            action: () => { },
            color: 'primary',
            customButton: (
              <GlobalSearcher
                filters={filters}
                triggerSearch={triggerSearch}
                dispatch={dispatch}
                disableSearch={loading}
                maxMonths={1}
                shortcuts={false}
              />
            ),
          },
        ]}
      />

      <EnhancedTable
        dataHeaders={[
          { key: 'typeId', label: 'ID Tipo' },
          { key: 'type', label: 'Tipo' },
          { key: 'docType', label: 'Tipo Doc.' },
          { key: 'folio', label: 'Folio Doc.' },
          { key: 'requestId', label: 'ID Solicitud' },
          { key: 'requestType', label: 'Tipo Solicitud' },
          {
            key: 'requestOperationDate',
            label: 'Fecha Solicitud Factoring',
            type: 'date',
          },
          { key: 'clientName', label: 'Cliente' },
          { key: 'clientIdentifier', label: 'Rut Cliente' },
          { key: 'businessName', label: 'Emisor' },
          { key: 'businessIdentifier', label: 'Rut Emisor' },
          { key: 'stakeholderName', label: 'Deudor' },
          { key: 'stakeholderIdentifier', label: 'Rut Deudor' },
          { key: 'fundName', label: 'Fondo' },
          { key: 'fundIdentifier', label: 'Rut Fondo' },
          { key: 'paidBy', label: 'Pagado por' },
          { key: 'paymentDate', label: 'Fecha Vencimiento Doc.', type: 'date' },
          { key: 'documentAmount', label: 'Monto Doc.', type: 'money' },
          {
            key: 'documentAdvanceAmount',
            label: 'Anticipo Doc.',
            type: 'money',
          },
          { key: 'amount', label: 'Monto Recaudado', type: 'money' },
          {
            key: 'conciliationDocumentAmount',
            label: 'Conciliado Doc.',
            type: 'money',
          },
          {
            key: 'conciliationArrearAmount',
            label: 'Conciliado Mora',
            type: 'money',
          },
          {
            key: 'conciliationDate',
            label: 'Recaudación',
            type: 'date',
            description:
              'Fecha en la que la recaudación fue registrada en Pruff',
          },
          {
            key: 'payInDate',
            label: 'Pago recibido',
            type: 'date',
            description:
              'Fecha en la que el pago fue recibido en la cuenta bancaria',
          },
          { key: 'executiveName', label: 'Ejecutivo' },
        ]}
        data={data}
        rowsPerPageDefault={25}
        lastElement
        exportToFile="xlsx"
        finalSumRow
      />
    </div>
  );
};

export default Collection;
